// apps/web/src/lib/briefing-context-bootstrap.ts
//
// KAN-661 U4 — the receiving half of `#briefing-context=<token>`.
// (Spec R14, R20, R31, R32, R36, R37, R38, R39; flow F5; AE11, AE12.)
//
// ---------------------------------------------------------------------------
// WHY THIS IS A MODULE BODY AND NOT A HOOK
// ---------------------------------------------------------------------------
//
// R14/R36 require the token to be out of the URL before telemetry, the service
// worker and React initialise. ES module imports are hoisted and every imported
// module body runs, in source order, before the FIRST statement of the importing
// module. So in `main.tsx` even a call placed above `Sentry.init()` is already
// too late — `@sentry/react` evaluated long before it. A hook inside `App` or
// `StoryDetailPage` is later still by several orders of magnitude.
//
// The only construction early enough is a side-effect import placed as the very
// first import statement in `main.tsx`, whose module body does the work. That is
// what the last line of this file is, and it is why the import in `main.tsx`
// carries a "must remain first" comment. The ordering is proved by observation
// in `__tests__/briefing-context-bootstrap.test.ts`, where mocked telemetry
// modules snapshot `location.hash` at the instant they evaluate.
//
// This module therefore imports NOTHING that could drag telemetry in with it.
// `@intelhub/shared/briefing-link` is pure (no imports at all, no Node builtins,
// no I/O) and is the shared contract for the fragment key and the token bound,
// so both ends cannot drift.
//
// ---------------------------------------------------------------------------
// WHY THE TOKEN IS OPAQUE HERE, AND WHERE `expiresAt` COMES FROM
// ---------------------------------------------------------------------------
//
// The browser never verifies the token and never reads a claim out of it. It
// cannot: verification needs the purpose-scoped HMAC key, and shipping that to a
// browser would make every token forgeable. So the token's own expiry is not
// knowable here.
//
// `expiresAt` on the pending record is therefore a LOCAL continuation bound —
// `now + BRIEFING_CONTEXT_PENDING_TTL_MS` — not a claim parsed from the token.
// It is much shorter than any token lifetime, so it satisfies R37's "scoped to
// story ID and token expiry" from the strict side: the local record dies well
// before the token does. The authoritative expiry check stays server-side, where
// the key is (R15).
//
// ---------------------------------------------------------------------------
// WHY sessionStorage AND NOTHING ELSE (R37)
// ---------------------------------------------------------------------------
//
// The token has to survive a full-page OIDC redirect in the same tab, and there
// is exactly one store with that shape: `sessionStorage` is per-tab, dies with
// the tab, and is never sent to a server. `localStorage` would outlive the tab
// and be shared across every tab on the origin; a cookie would be attached to
// every request and land in access logs; the URL would leak through history,
// bookmarks and `Referer`; the server session would bind the token to an
// identity, which is the one thing the design spent a whole token format
// avoiding. None of those are acceptable and none of them are used.

import {
  BRIEFING_CONTEXT_FRAGMENT_KEY,
  BRIEFING_CONTEXT_TOKEN_MAX_LENGTH,
} from '@intelhub/shared/briefing-link'

/** The one `sessionStorage` slot this module writes. Exported so tests can watch it. */
export const BRIEFING_CONTEXT_PENDING_STORAGE_KEY = 'intelhub.briefing-context.pending'

/**
 * How long a captured token may sit in `sessionStorage` waiting to be consumed.
 *
 * Ten minutes: an unhurried sign-in — read the email, pick a provider, complete
 * MFA, approve a push — fits inside it, and an abandoned tab left open on a
 * shared laptop does not keep the continuation alive for the rest of the day.
 * Expiry is never a dead end; the reader simply gets ordinary story detail.
 */
export const BRIEFING_CONTEXT_PENDING_TTL_MS = 10 * 60 * 1000

/** `#briefing-context=` — the exact prefix the controlled link builder writes. */
const FRAGMENT_PREFIX = `#${BRIEFING_CONTEXT_FRAGMENT_KEY}=`

/** The only route a briefing-context fragment is meaningful on. */
const STORY_PATH =
  /^\/stories\/([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})$/

/**
 * The alphabet an opaque token may use — the same one `buildBriefingLink`
 * enforces on the way out. This is a shape check, not verification: it exists so
 * that a fragment which cannot possibly be one of our tokens is discarded before
 * it is ever stored, not so that a well-shaped string is believed.
 */
const OPAQUE_TOKEN = /^[A-Za-z0-9._-]+$/

export interface PendingBriefingContext {
  /** `stories.id`, read from the PATH — never from the token, which is opaque. */
  storyId: string
  /** The opaque token, exactly as it arrived. */
  token: string
  /** Local continuation deadline in epoch ms. See the header. */
  expiresAt: number
}

// ---------------------------------------------------------------------------
// Pending-context storage
// ---------------------------------------------------------------------------

function isPending(value: unknown): value is PendingBriefingContext {
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as Record<string, unknown>
  return (
    typeof candidate.storyId === 'string' &&
    typeof candidate.token === 'string' &&
    typeof candidate.expiresAt === 'number' &&
    Number.isFinite(candidate.expiresAt)
  )
}

/**
 * Every access is wrapped: `sessionStorage` throws outright in some privacy
 * configurations. Losing the continuation is a degraded read, not an error — the
 * reader still gets ordinary story detail — so a throw here must never surface.
 */
export function clearPendingBriefingContext(): void {
  try {
    window.sessionStorage.removeItem(BRIEFING_CONTEXT_PENDING_STORAGE_KEY)
  } catch {
    /* storage unavailable: nothing was written, so nothing to clear */
  }
}

function writePending(pending: PendingBriefingContext): void {
  try {
    window.sessionStorage.setItem(
      BRIEFING_CONTEXT_PENDING_STORAGE_KEY,
      JSON.stringify(pending),
    )
  } catch {
    /* storage unavailable: continuation is a nicety, not a requirement */
  }
}

function readPending(now: number): PendingBriefingContext | null {
  let raw: string | null
  try {
    raw = window.sessionStorage.getItem(BRIEFING_CONTEXT_PENDING_STORAGE_KEY)
  } catch {
    return null
  }
  if (!raw) return null

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    clearPendingBriefingContext()
    return null
  }
  if (!isPending(parsed) || parsed.expiresAt <= now) {
    clearPendingBriefingContext()
    return null
  }
  return parsed
}

// ---------------------------------------------------------------------------
// The bootstrap itself
// ---------------------------------------------------------------------------

/**
 * Capture and strip `#briefing-context=<token>`, synchronously.
 *
 * THE STRIP IS UNCONDITIONAL AND HAPPENS FIRST. Whether the token is usable and
 * whether it may remain in the URL are different questions with different
 * answers: a malformed, oversize or wrong-route fragment is not stored, but it
 * is still removed. Storing is the narrow path; removing is the wide one.
 *
 * Exported so tests can drive it against a chosen URL. Production calls it once,
 * from this module's body.
 */
export function bootstrapBriefingContext(now: number = Date.now()): void {
  if (typeof window === 'undefined') return

  const { hash, pathname, search } = window.location
  // An unrelated fragment (`#sources`) is someone else's business — leave it.
  if (!hash.startsWith(FRAGMENT_PREFIX)) return

  try {
    window.history.replaceState(window.history.state, '', `${pathname}${search}`)
  } catch {
    /* if the URL cannot be rewritten, storing the token would only add a
       second copy of it; fall through and store nothing */
    return
  }

  const token = hash.slice(FRAGMENT_PREFIX.length)
  const story = STORY_PATH.exec(pathname)
  if (!story) return
  if (token.length === 0 || token.length > BRIEFING_CONTEXT_TOKEN_MAX_LENGTH) return
  if (!OPAQUE_TOKEN.test(token)) return

  writePending({
    storyId: story[1],
    token,
    expiresAt: now + BRIEFING_CONTEXT_PENDING_TTL_MS,
  })
}

/**
 * Take the pending context for `storyId`, if there is one, and end the
 * continuation either way.
 *
 * CONSUME-ONCE IS ENFORCED BY CLEARING BEFORE RETURNING, not by a caller
 * remembering to. R39 does not support reuse after the first consumption, and a
 * peek-then-clear API would make "exactly once" a property of every call site
 * instead of a property of this function.
 *
 * A STORY MISMATCH IS TERMINAL, not a miss (R38). If the reader is asking about
 * a different story, they have navigated away from the one the link was for, and
 * the continuation is over. Leaving it pending would let it re-attach to a later
 * visit that had nothing to do with the briefing click.
 *
 * This is the entry point U5 calls once the session has resolved to a subject.
 */
export function consumePendingBriefingContext(
  storyId: string,
  now: number = Date.now(),
): PendingBriefingContext | null {
  const pending = readPending(now)
  if (!pending) return null
  clearPendingBriefingContext()
  return pending.storyId === storyId ? pending : null
}

/**
 * End the continuation when the sign-in surface reports a refusal.
 *
 * `?error=` on the login surface is the only local signal that the IdP round
 * trip came back cancelled, failed or denied (R38). Everything else — expiry,
 * mismatch, sign-out, subject change — is observable elsewhere in this module;
 * this one is only observable there, which is why `LoginPage` calls it.
 */
export function clearPendingBriefingContextOnAuthError(search: string): void {
  if (new URLSearchParams(search).get('error')) {
    clearPendingBriefingContext()
  }
}

// ---------------------------------------------------------------------------
// In-flight cancellation and cache reset (R32)
// ---------------------------------------------------------------------------
//
// R32 is an ORDERING requirement with a specific failure it is written against:
// clear a cache first and a request already on the wire can land afterwards and
// repopulate it — under the NEW subject's cache key, with the OLD subject's
// data. So cancellation runs first, and `runBriefingContextRequest` additionally
// refuses to hand back any response whose request was cancelled while in flight.
// Either control alone would leave a window; together there is none.

const inFlight = new Set<AbortController>()
const cacheResets = new Set<() => void>()

/** Register an in-flight contextual request so a session change can cancel it. Returns an unregister. */
export function registerBriefingContextRequest(controller: AbortController): () => void {
  inFlight.add(controller)
  return () => {
    inFlight.delete(controller)
  }
}

/** Cancel every in-flight contextual request. Safe to call when there are none. */
export function cancelBriefingContextRequests(): void {
  for (const controller of [...inFlight]) {
    try {
      controller.abort()
    } catch {
      /* an already-aborted controller is the desired state, not an error */
    }
  }
  inFlight.clear()
}

/**
 * Register a cache-removal callback — U5 supplies the one that drops its
 * contextual query. Returns an unregister.
 */
export function registerBriefingContextCacheReset(reset: () => void): () => void {
  cacheResets.add(reset)
  return () => {
    cacheResets.delete(reset)
  }
}

/**
 * Run a contextual request under cancellation.
 *
 * Returns `null` — never the payload — if the request was cancelled while in
 * flight, so a late response cannot be written into another session's cache even
 * if the underlying transport ignored the signal. This is the wrapper U5 should
 * use for the authenticated context POST.
 */
export async function runBriefingContextRequest<T>(
  fn: (signal: AbortSignal) => Promise<T>,
): Promise<T | null> {
  const controller = new AbortController()
  const unregister = registerBriefingContextRequest(controller)
  try {
    const result = await fn(controller.signal)
    return controller.signal.aborted ? null : result
  } catch (error) {
    if (controller.signal.aborted) return null
    throw error
  } finally {
    unregister()
  }
}

/**
 * Tear down everything session-scoped: cancel, then drop caches, then clear the
 * pending token. Called on sign-out and on an auth-subject change.
 */
export function endBriefingContextSession(): void {
  cancelBriefingContextRequests()
  for (const reset of [...cacheResets]) {
    try {
      reset()
    } catch {
      /* one consumer's failed cleanup must not skip the next one's */
    }
  }
  clearPendingBriefingContext()
}

// ---------------------------------------------------------------------------
// Auth bridge (R38, R39)
// ---------------------------------------------------------------------------

/**
 * `undefined` means "no session has resolved in this document yet" and is
 * deliberately distinct from `null`, which means "resolved, and signed out".
 * Collapsing the two would make the very first `/me` resolution look like a
 * transition and tear down the continuation the moment the page loaded.
 */
let lastSubject: string | null | undefined

/**
 * Called by `AuthProvider` every time `/me` resolves.
 *
 * WHAT COUNTS AS A CHANGE, AND WHAT DOES NOT:
 *
 *  - `undefined -> anything` is the first resolution of this document. On the
 *    return leg of the OIDC round trip this is where the reader arrives
 *    authenticated, and on the outbound leg it is where they arrive signed out
 *    and are about to be sent to the IdP. Neither may discard the pending
 *    context — F5 exists precisely to carry it across that boundary.
 *  - `null -> subject` is authentication completing inside one document, not a
 *    change of person. There is no previous subject's state to protect.
 *  - `subject -> anything else` is a real change of person (or a session that
 *    expired). Cancel, drop caches, clear pending — in that order.
 */
export function notifyBriefingContextAuthSubject(subject: string | null): void {
  const previous = lastSubject
  lastSubject = subject
  if (previous === undefined || previous === null || previous === subject) return
  // NOTE the assignment above happens FIRST and is not undone here: a teardown
  // that also reset the bookkeeping would make the NEXT change look like a first
  // resolution, and B -> C would silently skip cancellation.
  endBriefingContextSession()
}

/** Test seam. Mirrors `__resetForTests` in `@intelhub/analytics`. */
export function __resetBriefingContextForTests(): void {
  inFlight.clear()
  cacheResets.clear()
  lastSubject = undefined
  clearPendingBriefingContext()
}

// The side effect. Everything above is exported for testing and for U5; THIS is
// what makes the ordering guarantee true in production.
bootstrapBriefingContext()
